/**
 * Cedar Model Intelligence — Admin API Service
 *
 * Typed methods for all LLM optimization agent endpoints.
 * Uses the same auth pattern as other admin services.
 */

import { storage } from './storage';
import { API_BASE_URL } from '../utils/apiConfig';

const BASE = `${API_BASE_URL}/api/admin/llm-optimization`;

async function headers(): Promise<Record<string, string>> {
  const token = await storage.getItem('admin_token');
  if (!token) throw new Error('Admin auth token not found — please log in');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: await headers(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`API error ${res.status}: ${await res.text()}`);
  return res.json();
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: await headers(),
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`API error ${res.status}: ${await res.text()}`);
  return res.json();
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'PATCH',
    headers: await headers(),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API error ${res.status}: ${await res.text()}`);
  return res.json();
}

// ── Types ──────────────────────────────────────────────

export interface Alert {
  id: string;
  insight_id: string | null;
  alert_type: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  status: 'active' | 'acknowledged' | 'resolved' | 'dismissed';
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  resolved_at: string | null;
  created_at: string;
  insight_type?: string;
  affected_calls?: string[];
  cost_delta_monthly?: number;
  confidence?: string;
}

export interface Insight {
  id: string;
  research_id: string | null;
  insight_type: string;
  affected_calls: string[];
  affected_call_count: number;
  current_config: Record<string, unknown>;
  proposed_config: Record<string, unknown>;
  cost_delta_monthly: number;
  speed_delta_percent: number;
  quality_assessment: string;
  confidence: 'high' | 'medium' | 'low';
  status: string;
  profile_id: string | null;
  created_at: string;
  research_model?: string;
  research_title?: string;
  vendor?: string;
}

export interface Research {
  id: string;
  research_type: string;
  vendor: string;
  model_name: string;
  title: string;
  summary: string;
  key_findings: unknown;
  source_urls: string[];
  affected_call_names: string[] | null;
  estimated_impact: string;
  cost_impact_estimate: number | null;
  processed: boolean;
  created_at: string;
}

export interface LandscapeModel {
  id: string;
  vendor: string;
  model_name: string;
  display_name: string;
  model_family: string;
  input_cost_per_mtok: number | null;
  output_cost_per_mtok: number | null;
  cached_input_cost_per_mtok: number | null;
  max_input_tokens: number | null;
  max_output_tokens: number | null;
  tokens_per_second: number | null;
  ttft_seconds: number | null;
  status: string;
  supports_streaming: boolean;
  supports_json_mode: boolean;
  supports_structured_outputs: boolean;
  supports_function_calling: boolean;
  supports_reasoning: boolean;
  supports_vision: boolean;
  reasoning_effort_levels: string[] | null;
  notes: string | null;
}

export interface AgentRun {
  id: string;
  agent_type: string;
  trigger_type: string;
  searches_performed: number;
  research_calls: number;
  insights_generated: number;
  alerts_created: number;
  total_cost_usd: number;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
  status: string;
  error_message: string | null;
}

export interface DashboardData {
  alerts: Alert[];
  insights: Insight[];
  research: Research[];
  run_stats: {
    total_runs: number;
    total_cost: number;
    total_insights: number;
    total_alerts: number;
    last_run: string | null;
  };
  landscape: { vendor: string; model_count: number; available: number }[];
}

export interface Digest {
  generated_at: string;
  alerts: { new_count: number; total_active: number; new_alerts: Alert[] };
  cost_trend: {
    yesterday_usd: number;
    week_avg_usd: number;
    delta_percent: number;
    top_call: { call_name: string; total_cost: number } | null;
  };
  pending_actions: { pending_insights: number; active_test_profiles: number };
  landscape_changes: { count: number; changes: Research[] };
  agent_status: { last_run: AgentRun | null; week_agent_cost_usd: number };
  notification_text: string;
  email_text: string;
}

export interface SavingsBreakdownEntry {
  count: number;
  estimated_cost_per_skip_usd: number;
  estimated_savings_usd: number;
}

export interface SavingsSummary {
  period_days: number;
  breakdown: {
    early_dedup?: SavingsBreakdownEntry;
    qia_non_chat?: SavingsBreakdownEntry;
    elite_analysis_historical?: SavingsBreakdownEntry;
    goal_progress_historical?: SavingsBreakdownEntry;
    [key: string]: SavingsBreakdownEntry | undefined;
  };
  total_estimated_savings_usd: number;
  calculation_note: string;
}

// ── API Methods ────────────────────────────────────────

export const modelIntelligence = {
  getDashboard: () => get<DashboardData>('/dashboard'),

  getAlerts: (status?: string) =>
    get<{ alerts: Alert[]; count: number }>(`/alerts${status ? `?status=${status}` : ''}`),

  updateAlert: (alertId: string, action: string, reason?: string) =>
    patch<{ status: string }>(`/alerts/${alertId}`, { action, reason }),

  getInsights: (status?: string, confidence?: string) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (confidence) params.set('confidence', confidence);
    const qs = params.toString();
    return get<{ insights: Insight[]; count: number }>(`/insights${qs ? `?${qs}` : ''}`);
  },

  getResearch: (vendor?: string, researchType?: string) => {
    const params = new URLSearchParams();
    if (vendor) params.set('vendor', vendor);
    if (researchType) params.set('research_type', researchType);
    const qs = params.toString();
    return get<{ research: Research[]; count: number }>(`/research${qs ? `?${qs}` : ''}`);
  },

  triggerResearch: (vendor: string, modelName: string) =>
    post<unknown>('/research/trigger', { vendor, model_name: modelName }),

  getLandscape: (vendor?: string) =>
    get<{ models: LandscapeModel[]; count: number }>(`/landscape${vendor ? `?vendor=${vendor}` : ''}`),

  getAbTests: () => get<{ tests: unknown[]; count: number }>('/ab-tests'),

  getAgentRuns: (agentType?: string) =>
    get<{ runs: AgentRun[]; count: number }>(`/agent-runs${agentType ? `?agent_type=${agentType}` : ''}`),

  getDigest: () => get<Digest>('/digest'),

  runPipeline: () => post<unknown>('/pipeline/run'),

  generateProfile: () => post<unknown>('/pipeline/generate-profile'),

  getSavingsSummary: (days = 30) => get<SavingsSummary>(`/savings-summary?days=${days}`),
};
